import React, { useState, useEffect, useRef } from 'react'
import { useDataContext } from 'components/document/contexts/data'
import { useApplicationStore } from 'hooks/application'
import api from 'helpers/api'
import useProject from 'hooks/useProject'
import Button from 'components/button'
import { validateSourceUrl } from 'helpers/utils'

const DEFAULT_BANNER_HEIGHT = 300
const MIN_BANNER_HEIGHT = 80
const MAX_BANNER_HEIGHT = 600

const Image: React.FC = () => {
  const { project } = useProject()
  const { process, updateProcess, isAdmin, editMode } = useDataContext()
  const { displayErrorMessage } = useApplicationStore()
  const inputFile = useRef<HTMLInputElement>(null)

  const [src, setSrc] = useState<string>()
  const [ready, setReady] = useState<boolean>(false)

  const bannerHeight = process?.bannerHeight
  const isHidden = bannerHeight === 0
  const displayHeight = bannerHeight || DEFAULT_BANNER_HEIGHT

  const [dragHeight, setDragHeight] = useState<number | null>(null)
  const isDragging = dragHeight !== null
  const dragStartY = useRef(0)
  const dragStartHeight = useRef(0)

  const currentHeight = dragHeight ?? displayHeight

  const onImageClick = () => {
    if (inputFile.current) {
      inputFile.current.click()
    }
  }

  const onImageChange = async (e: any) => {
    if (process) {
      const file = e.target.files[0]
      e.target.value = ''

      try {
        const response = await api.uploadFile(file, [{ resource: 'process', publicId: process.publicId }])
        updateProcess({ ...process, logo: response.data.url })
      } catch (e) {
        displayErrorMessage(e)
      }
    }
  }

  const signUrl = async () => {
    if (process && process.logo) {
      try {
        const signedFileTokenResponse = await api.signUrl(process.logo)
        setSrc(signedFileTokenResponse.data.url)
        setReady(true)
      } catch (err) {
        displayErrorMessage(err)
        setSrc(undefined)
        setReady(false)
        return
      }
    }
    if (process && !process.logo && project && project.defaultBanner) {
      try {
        const signedFileTokenResponse = await api.signUrl(project.defaultBanner)
        setSrc(signedFileTokenResponse.data.url)
        setReady(true)
      } catch (err) {
        displayErrorMessage(err)
        setSrc(undefined)
        setReady(false)
        return
      }
    }
  }

  useEffect(() => {
    if (process && process.logo) {
      signUrl().catch(() => {})
    } else if (project && project.defaultBanner) {
      signUrl().catch(() => {})
    }
  }, [process && process.logo, project && project.defaultBanner])

  useEffect(() => {
    if (!isDragging) return
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging])

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragStartY.current = e.clientY
    dragStartHeight.current = currentHeight
    setDragHeight(currentHeight)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return
    const delta = e.clientY - dragStartY.current
    const raw = dragStartHeight.current + delta
    setDragHeight(Math.min(MAX_BANNER_HEIGHT, Math.max(MIN_BANNER_HEIGHT, raw)))
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    e.currentTarget.releasePointerCapture(e.pointerId)
    if (process && dragHeight !== null) {
      updateProcess({ ...process, bannerHeight: dragHeight })
    }
    setDragHeight(null)
  }

  const handleLostPointerCapture = () => {
    setDragHeight(null)
  }

  const handleHideBanner = () => {
    if (process) {
      updateProcess({ ...process, bannerHeight: 0 })
    }
  }

  const handleShowBanner = () => {
    if (process) {
      updateProcess({ ...process, bannerHeight: DEFAULT_BANNER_HEIGHT })
    }
  }

  if (isHidden) {
    if (isAdmin && editMode) {
      return (
        <div className="group relative flex h-6 w-full items-center justify-center">
          <Button className="absolute text-xs" internalType="secondary" onClick={handleShowBanner}>
            Show Banner
          </Button>
        </div>
      )
    }
    return null
  }

  return (
    <div className="relative w-full" style={{ height: currentHeight }}>
      {isAdmin && editMode && (
        <div className="absolute bg-surface text-sm flex rounded overflow-hidden transition-all whitespace-nowrap mr-[150px] p-[5px] min-h-6 right-2 bottom-5 z-[1] max-w-[calc(100%_-_16px)] text-muted">
          <Button onClick={onImageClick}>Change Image</Button>
          {process && process.logo && (
            <Button
              className="ml-[5px]"
              internalType="danger"
              onClick={() => {
                if (window.confirm('Are you sure you want to remove the image?')) {
                  if (process) {
                    updateProcess({ ...process, logo: null })
                    setReady(false)
                    setSrc(undefined)
                  }
                }
              }}
            >
              Remove Image
            </Button>
          )}
          <Button className="ml-[5px]" internalType="secondary" onClick={handleHideBanner}>
            Hide Banner
          </Button>
          <input type="file" id="file" ref={inputFile} onChange={onImageChange} className="hidden" accept="image/*" />
        </div>
      )}
      {ready && src ? (
        <img
          src={validateSourceUrl(src)}
          className="h-full w-full object-cover"
          style={isDragging ? { pointerEvents: 'none' } : undefined}
          alt={process?.logo || project?.defaultBanner || 'Document banner image'}
        />
      ) : (
        <div className="h-full w-full" style={{ backgroundColor: project.primaryColour }} />
      )}
      {isAdmin && editMode && (
        <div
          className="group absolute bottom-0 left-0 z-[2] flex h-3 w-full cursor-row-resize items-center justify-center"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onLostPointerCapture={handleLostPointerCapture}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize banner height"
        >
          <div className="h-[3px] w-12 rounded-full bg-surface opacity-0 shadow transition-opacity group-hover:opacity-80" />
        </div>
      )}
    </div>
  )
}

export default Image
